import { inject, Injectable } from '@angular/core';
import { omit } from 'lodash-es';
import { AddressService } from '@ireckonu/shared-ui';

import { HARDCODED_LANGUAGES } from '@shared/app-config/consts';

import {
  NameInterface,
  PersonDataInterface,
  PersonLookupRequestInterface,
  ProfileApiInterface,
  RFM_LOYALTY_LEVEL,
  RfmTierInterface,
} from '../models';

/**
 * Minimum shape the mapper needs from any person-search form. Both
 * `LoyaltySearchQueryApiInterface` and `ProfileSearchQueryApiInterface`
 * satisfy this.
 */
export interface PersonLookupSearchInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  membershipId?: string;
  personId?: string;
  personIds?: number[];
}

// TODO: profile lookup params can be optimized based on the use case (e.g. loyalty lookup doesn't need addresses or consents).
// It can be done after the logic is stable and we have more clarity on what fields are actually needed in different parts of the app.
const FULL_LOOKUP_PARAMS: PersonLookupRequestInterface['ExtraLookupParams'] = {
  IncludeAddresses: true,
  IncludePhoneNumbers: true,
  IncludeAttributes: true,
  IncludeDataConsents: true,
  IncludeStaySummaries: true,
  IncludeLoyaltyLevels: true,
  IncludeMemberships: true,
};

@Injectable({ providedIn: 'root' })
export class PersonLookupMappingService {
  private readonly addressService = inject(AddressService);

  /**
   * Maps an array of PersonDataInterface to ProfileApiInterface[].
   */
  mapPersonsToProfiles(persons: PersonDataInterface[]): ProfileApiInterface[] {
    return persons.map((person) => this.mapPersonToProfile(person));
  }

  /**
   * Maps the component search query to the API request format.
   */
  mapSearchQueryToRequest(
    query: PersonLookupSearchInput,
    extraLookupParams: PersonLookupRequestInterface['ExtraLookupParams'] = FULL_LOOKUP_PARAMS,
  ): PersonLookupRequestInterface {
    const request: PersonLookupRequestInterface = {
      ExtraLookupParams: { ...extraLookupParams },
    };

    if (query.firstName) {
      request.FirstName = query.firstName;
    }
    if (query.lastName) {
      request.LastName = query.lastName;
    }
    if (query.email) {
      request.EmailAddress = query.email;
    }
    if (query.phone) {
      request.PhoneNumber = query.phone;
    }
    if (query.membershipId) {
      request.MembershipCode = query.membershipId;
    }
    if (query.personId) {
      const personId = Number(query.personId);
      if (!Number.isNaN(personId)) {
        request.PersonId = personId;
      }
    }
    if (query.personIds) {
      request.PersonIds = query.personIds;
    }

    return request;
  }

  /**
   * Builds a lookup request to fetch a single person by id.
   */
  buildLookupByIdRequest(
    profileId: string | number,
    extraLookupParams: PersonLookupRequestInterface['ExtraLookupParams'] = FULL_LOOKUP_PARAMS,
  ): PersonLookupRequestInterface {
    return {
      PersonId: Number(profileId),
      ExtraLookupParams: { ...extraLookupParams },
    };
  }

  mapPersonToProfile(person: PersonDataInterface): ProfileApiInterface {
    const name = this.getDisplayName(person);
    const kanjiName = this.findNameByType(person.Names, 'kanji');
    const katakanaName = this.findNameByType(person.Names, 'katakana');

    const email = this.getEmail(person);

    const phone = this.getPhone(person);

    const address = this.getPrimaryAddress(person);

    const avatar = this.getAvatarUrl(person);

    const primaryMembership = person.Memberships?.find((m) => m.IsPrimary);
    const firstMembership = person.Memberships?.[0];

    const rfmLoyaltyLevel = person.LoyaltyLevels?.find(
      (level) =>
        level.Source === RFM_LOYALTY_LEVEL.source &&
        level.TypeCode === RFM_LOYALTY_LEVEL.typeCode,
    );

    return {
      id: String(person.PersonId),
      name,
      kanjiName: this.formatJapaneseName(kanjiName),
      katakanaName: this.formatJapaneseName(katakanaName),
      avatar,
      email,
      phone,
      address,
      names: person.Names?.map((n) => ({
        type: n.TypeCode,
        title: n.Title,
        firstName: n.FirstName,
        middleName: n.MiddleName,
        lastName: n.LastName,
        suffix: n.Suffix,
      })),
      emails: person.EmailAddresses?.map((e) => ({
        type: e.TypeCode,
        value: e.Value,
        isPrimary: e.IsPrimary,
      })),
      phones: person.PhoneNumbers?.map((p) => ({
        type: p.TypeCode,
        number: p.Number,
        countryAccessCode: p.CountryAccessCode,
        isPrimary: p.IsPrimary,
      })),
      addresses: person.Addresses?.map((a) => ({
        address: this.addressService.getAddressLine(a),
        type: a.TypeCode,
        isPrimary: a.IsPrimary,
      })),
      dateOfBirth: person.DateOfBirth ?? undefined,
      language:
        HARDCODED_LANGUAGES.find((lang) => lang.Value === person.LanguageCode)
          ?.Name ??
        person.LanguageCode ??
        undefined,
      nationality: person.CountryCode ?? undefined,
      membershipId: primaryMembership?.Code ?? firstMembership?.Code,
      membershipTier: primaryMembership?.Level ?? firstMembership?.Level,
      rfmTier: this.mapRfmTier(rfmLoyaltyLevel),
    };
  }

  static hasSearchValues(query: PersonLookupSearchInput): boolean {
    const searchFields = omit(query, ['sort', 'sortDirection']);
    return Object.values(searchFields).some((value) => !!value);
  }

  private getDisplayName(person: PersonDataInterface): string | undefined {
    const parts: string[] = [];
    if (person.Title) {
      parts.push(person.Title);
    }
    if (person.FirstName) {
      parts.push(person.FirstName);
    }
    if (person.LastName) {
      parts.push(person.LastName);
    }
    return parts.length > 0 ? parts.join(' ') : undefined;
  }

  private getEmail(person: PersonDataInterface): string | undefined {
    const primaryEmail = person.EmailAddresses?.find((e) => e.IsPrimary);
    return (
      primaryEmail?.Value ||
      person.EmailAddress ||
      person.EmailAddresses?.[0]?.Value
    );
  }

  /**
   * Gets phone from PhoneNumbers array.
   */
  private getPhone(person: PersonDataInterface): string | undefined {
    const primaryPhone = person.PhoneNumbers?.find((p) => p.IsPrimary);
    return primaryPhone?.Number || person.PhoneNumbers?.[0]?.Number;
  }

  /**
   * Gets the primary address as a single formatted line.
   */
  private getPrimaryAddress(person: PersonDataInterface): string | undefined {
    const primary =
      person.Addresses?.find((a) => a.IsPrimary) ?? person.Addresses?.[0];
    if (!primary) {
      return undefined;
    }
    return this.addressService.getAddressLine(primary);
  }

  /**
   * Gets the avatar URL from Attributes array where Code is "Url" and TypeCode is "Image".
   */
  private getAvatarUrl(person: PersonDataInterface): string | undefined {
    const imageAttribute = person.Attributes?.find(
      (attr) => attr.Code === 'Url' && attr.TypeCode === 'Image',
    );
    return imageAttribute?.Value ?? undefined;
  }

  private findNameByType(
    names: NameInterface[] | undefined,
    typeCode: string,
  ): NameInterface | undefined {
    if (!names?.length) {
      return undefined;
    }

    const typeCodeLower = typeCode.toLowerCase();

    // Case-insensitive matching for type code
    if (typeCodeLower === 'default') {
      return names.find(
        (n) =>
          !n.TypeCode ||
          n.TypeCode.toLowerCase() === 'default' ||
          n.TypeCode === '',
      );
    }
    return names.find((n) => n.TypeCode?.toLowerCase() === typeCodeLower);
  }

  /**
   * Formats Japanese names (kanji/katakana) combining FirstName and LastName.
   */
  private formatJapaneseName(
    name: NameInterface | undefined,
  ): string | undefined {
    if (!name) {
      return undefined;
    }

    const parts: string[] = [];
    if (name.LastName) {
      parts.push(name.LastName);
    }
    if (name.FirstName) {
      parts.push(name.FirstName);
    }

    return parts.length > 0 ? parts.join(' ') : undefined;
  }

  private mapRfmTier(loyaltyLevel?: {
    LevelCode?: string;
    Score?: number;
  }): RfmTierInterface | undefined {
    if (!loyaltyLevel?.LevelCode) {
      return undefined;
    }

    return {
      code: loyaltyLevel.LevelCode,
      label: loyaltyLevel.LevelCode,
      score: loyaltyLevel.Score,
    };
  }
}
